import type { Continuity, Item } from '@dissidium/contracts';
import { transferItem, validateLedger } from '../continuity/ledger.ts';
import { BASE_ACTIONS, findCapability, itemDefinition, kitTemplateIdOf } from './catalogue.ts';
import type { ActionId, ItemTemplateId, Slot } from './types.ts';

function fail(message: string): never { throw new Error(message); }

export function ownedItems(c: Continuity, personId: string): Item[] {
  return Object.values(c.items)
    .filter(item => item.allocation.kind === 'person' && item.allocation.id === personId)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function equip(input: Continuity, personId: string, itemId: string): Continuity {
  const next = validateLedger(input);
  const person = next.persons[personId] ?? fail('Unknown person');
  if (person.lifecycle === 'dead') fail(`${person.current.name} is dead and cannot equip gear`);
  const item = next.items[itemId] ?? fail('Unknown item');
  if (!(item.allocation.kind === 'person' && item.allocation.id === personId)) fail(`${item.name} is not carried by ${person.current.name}`);
  const templateId = kitTemplateIdOf(item) ?? fail(`${item.name} is not D01.2 kit gear`);
  const definition = itemDefinition(templateId);
  if (definition.slot === null) fail(`${definition.name} has no equipment slot`);
  const slot: Slot = definition.slot;
  for (const otherId of [...person.current.equipment]) {
    if (otherId === itemId) continue;
    const other = next.items[otherId];
    if (!other) continue;
    const otherTemplate = kitTemplateIdOf(other);
    if (otherTemplate && itemDefinition(otherTemplate).slot === slot) person.current.equipment = person.current.equipment.filter(id => id !== otherId);
  }
  if (!person.current.equipment.includes(itemId)) person.current.equipment.push(itemId);
  return validateLedger(next);
}

export function unequip(input: Continuity, personId: string, itemId: string): Continuity {
  const next = validateLedger(input);
  const person = next.persons[personId] ?? fail('Unknown person');
  if (!person.current.equipment.includes(itemId)) fail(`${person.current.name} does not have that item equipped`);
  person.current.equipment = person.current.equipment.filter(id => id !== itemId);
  return validateLedger(next);
}

export function availableActions(c: Continuity, personId: string): ActionId[] {
  const person = c.persons[personId] ?? fail('Unknown person');
  const actions = new Set<ActionId>(BASE_ACTIONS);
  const equippedSlots = new Set<Slot>();
  for (const itemId of person.current.equipment) {
    const item = c.items[itemId];
    if (!item) continue;
    const templateId = kitTemplateIdOf(item);
    if (!templateId) continue;
    const definition = itemDefinition(templateId);
    for (const grant of definition.grants) actions.add(grant);
    if (definition.slot !== null) equippedSlots.add(definition.slot);
  }
  for (const ability of person.current.abilities) {
    const capability = findCapability(ability);
    if (!capability) continue;
    if (capability.requiresSlot !== null && !equippedSlots.has(capability.requiresSlot)) continue;
    for (const grant of capability.grants) actions.add(grant);
  }
  return [...actions].sort();
}

export function consumeItem(input: Continuity, personId: string, itemId: string): { continuity: Continuity; fact: string } {
  const c = validateLedger(input);
  const item = c.items[itemId] ?? fail('Unknown item');
  if (!(item.allocation.kind === 'person' && item.allocation.id === personId)) fail(`${item.name} is not carried by that person`);
  const templateId = kitTemplateIdOf(item) ?? fail(`${item.name} is not D01.2 kit gear`);
  const definition = itemDefinition(templateId);
  if (!definition.consumable) fail(`${definition.name} is not consumable`);
  if (item.units !== 1) fail('Consumables are instantiated as single finite units');
  const continuity = transferItem(c, itemId, { kind: 'person', id: personId }, { kind: 'consumed', id: `effect:${definition.consumable.fact}` });
  return { continuity, fact: definition.consumable.fact };
}

export function tradeItem(c: Continuity, itemId: string, sellerId: string, buyerId: string): Continuity {
  return transferItem(c, itemId, { kind: 'person', id: sellerId }, { kind: 'person', id: buyerId });
}

export function preparedTemplateIds(c: Continuity, personId: string): ItemTemplateId[] {
  const templates = new Set<ItemTemplateId>();
  for (const item of ownedItems(c, personId)) {
    const templateId = kitTemplateIdOf(item);
    if (templateId) templates.add(templateId);
  }
  return [...templates].sort();
}
